import React from 'react';

const ANIMATION_DURATION_MS = 1000;

export const AnimatedNumber: React.FC<{ value: number; durationMs?: number }> = ({ value, durationMs = ANIMATION_DURATION_MS }) => {
    const [displayValue, setDisplayValue] = React.useState<number>(value);
    const [oldValue, setOldValue] = React.useState<number>(value);
    const [newValue, setNewValue] = React.useState<number>(value);
    const [translateYPercent, setTranslateYPercent] = React.useState<number>(0);
    const [animating, setAnimating] = React.useState<boolean>(false);
    const timeoutRef = React.useRef<number | undefined>(undefined);

    React.useEffect(() => {
        if (value === displayValue) return;

        if (timeoutRef.current !== undefined) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
        }

        const from = displayValue;
        const to = value;

        // Arrange the two rows as: [new, old].
        // Start with old visible (translate -100%), then animate to new (translate 0%).
        setOldValue(from);
        setNewValue(to);
        setAnimating(false);
        setTranslateYPercent(-100);

        const raf1 = window.requestAnimationFrame(() => {
            const raf2 = window.requestAnimationFrame(() => {
                setAnimating(true);
                setTranslateYPercent(0);
            });
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            return () => window.cancelAnimationFrame(raf2);
        });

        timeoutRef.current = window.setTimeout(() => {
            setDisplayValue(to);
            setOldValue(to);
            setNewValue(to);
            setAnimating(false);
            setTranslateYPercent(0);
            timeoutRef.current = undefined;
        }, durationMs);

        return () => {
            window.cancelAnimationFrame(raf1);
            if (timeoutRef.current !== undefined) {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = undefined;
            }
        };
    }, [value, displayValue, durationMs]);

    return (
        <div className="valueWindow" aria-live="polite">
            <div
                className="valueInner"
                style={{
                    transform: `translateY(${translateYPercent}%)`,
                    transition: animating ? `transform ${durationMs}ms ease-in-out` : 'none',
                }}
            >
                <div className="valueRow">{newValue}</div>
                <div className="valueRow">{oldValue}</div>
            </div>
        </div>
    );
};
